import { useEffect, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { Loader2, ArrowRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useUserAuth } from '../contexts/UserAuthContext';
import { PERSONAS } from './PersonaPicker';
import { resolveImageUrl } from './ImageUpload';
import AthleteOnboardingFlow from './onboarding/AthleteOnboardingFlow';
import { journeyAttempt } from '../lib/journey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter765 · Jay (day before launch): Fan never actually connected to
// anything (no dedicated fan flow exists), and having Agent + Fan next
// to Athlete on the very first screen new users see — right as ads start
// running — made the whole picker look broken/unfinished. Athlete-only
// for now; a parent or fan creating a profile ON BEHALF of their athlete
// is still fully supported, just as the athlete persona (see the note
// below), not a separate account type. Filtering PersonaPicker's shared
// PERSONAS list here (rather than removing them from that shared array)
// keeps every OTHER surface that reads PERSONAS — the profile persona
// switcher, agent accounts that already exist — completely unaffected.
const ONBOARDING_PERSONA_IDS = ['athlete'];

// iter754 · What tapping the small arrow on each tile reveals — Jay
// wants each persona to spell out concretely what that account gets,
// not just a one-line blurb.
const PERSONA_PERKS = {
  athlete: {
    perks: [
      'FLYT ID™ — your public bio link',
      'Network Profile',
      'Media Hub — every game photo in one place',
      'Analytics — track your reach',
      'Daily Brief — your sports catch-up',
    ],
    note: "Help your athlete get started. Create and manage their FLYT profile on their behalf.",
  },
};

/**
 * PersonaWelcomeModal · iter648 · Apple-style black/white liquid glass.
 * ─────────────────────────────────────────────────────────────────────
 * Prompts a signed-in user to pick their persona so the rest of the
 * site tailors itself. Redesigned to match the Sports & Teams / School
 * Settings aesthetic — pure neutral, no colored accents, small tiles,
 * white "Continue" text.
 */
const PersonaWelcomeModal = () => {
  const { user, updateProfile } = useUserAuth();
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  // iter · Athletes get a 4-step "build your FLYT ID" flow immediately
  // after picking the persona, instead of dropping straight back into
  // whatever page they were on with an empty profile. Gated on this
  // local flag (not `shouldShow`) so it keeps rendering even once
  // `user.persona === 'athlete'` would otherwise hide this modal.
  const [showAthleteOnboarding, setShowAthleteOnboarding] = useState(false);

  // Users who already have an agent/brand/athlete portal linked to their
  // email have implicitly declared their role — the quiz is redundant and
  // interrupts them. Fetch /me/portals once and skip the modal if any
  // portal exists. iter640 · Also skip when the account holds any
  // school_membership (photographer, coach, etc.) — program staff are
  // grown-ups who shouldn't be forced into an athlete/fan quiz to reach
  // the dashboard the school just invited them to.
  const [hasPortal, setHasPortal] = useState(null);   // null = unknown
  useEffect(() => {
    if (!user) { setHasPortal(null); return; }
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/me/portals`, { withCredentials: true }).then(r => (r.data.portals || []).length > 0).catch(() => false),
      axios.get(`${API}/me/school-memberships`, { withCredentials: true }).then(r => (r.data.memberships || []).length > 0).catch(() => false),
    ]).then(([hasP, hasMembership]) => {
      if (!cancelled) setHasPortal(hasP || hasMembership);
    });
    return () => { cancelled = true; };
  }, [user?.user_id]);

  // Suppress on routes where the modal would intercept a primary user action
  // (agent/brand signup + dashboards). Users are here to complete a specific
  // flow and the persona quiz breaks that flow.
  const suppressedRoute = typeof window !== 'undefined' && (
    /^\/(agent|brand)\/(signup|dashboard)/.test(window.location.pathname) ||
    /^\/athlete\/(me|dashboard)/.test(window.location.pathname) ||
    /^\/roster\/messages/.test(window.location.pathname)
  );

  // iter · Persona is mandatory — the whole app tailors itself off it,
  // so the modal stays up (no skip / X) until a persona is chosen. It
  // auto-hides once `user.persona` is set by pick().
  const shouldShow = !!user && !user.persona && !user.is_verified_athlete
    && !suppressedRoute && hasPortal === false;

  // Lock body scroll while the modal is open
  useEffect(() => {
    if (!shouldShow) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [shouldShow]);

  if (showAthleteOnboarding) {
    return <AthleteOnboardingFlow onDone={() => setShowAthleteOnboarding(false)} />;
  }

  if (!shouldShow) return null;

  const pick = async (id) => {
    setSavingId(id);
    // iter880 · App Health journey tracking (onboarding step).
    const j = journeyAttempt('persona_pick');
    try {
      try {
        await updateProfile({ persona: id });
      } catch (e) { j.fail(e); throw e; }
      j.ok();
      // Athletes go straight into the "build your FLYT ID" flow instead
      // of a toast + close — that flow is where the excitement/value
      // actually lives, so skip the generic confirmation for this one.
      if (id === 'athlete') {
        setShowAthleteOnboarding(true);
        return;
      }
      const p = PERSONAS.find((x) => x.id === id);
      toast.success(`You're set as ${p?.label}`, { description: 'We just tuned the site for you.' });
      // iter322 · Recruiters need to finish an org-affiliation form
      // and clear verification before they can use the dashboard —
      // route them straight there instead of dumping them on the
      // fan/athlete home so they hit the funnel end-to-end.
      if (id === 'recruiter' && typeof window !== 'undefined') {
        window.location.assign('/recruiter/signup');
      }
    } catch {
      toast.error('Could not save. Try again.');
    } finally {
      setSavingId(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-6 animate-fade-in text-white"
      style={{ background: '#050505' }}
      data-testid="persona-welcome-modal"
    >
      <div
        className="relative w-full sm:max-w-md max-h-[95vh] overflow-y-auto"
      >
        <div className="relative px-6 pt-2 pb-5 text-center">
          {user?.picture && (
            <img loading="lazy" decoding="async"
              src={resolveImageUrl(user.picture, 160)}
              alt=""
              className="w-14 h-14 rounded-full mx-auto mb-3 ring-2 ring-white/15 ring-offset-2 ring-offset-black"
            />
          )}
          <p className="text-[10px] uppercase tracking-[0.32em] mb-2 font-black text-white/70">
            Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl uppercase leading-[0.95] mb-2 text-white">
            Let&rsquo;s build your FLYT ID
          </h2>
          <p className="text-sm max-w-sm mx-auto text-white/65 leading-relaxed">
            Your public profile, career hub, and one link for everything.
          </p>
        </div>

        <div className="relative px-6 pb-8">
          {/* iter765 · Jay (day before launch): one big pill instead of a
              3-up grid — Athlete is the only onboarding path now, so this
              should read like the primary CTA it is, not a "choice"
              tile. Parent/fan account-creation is still fully supported —
              it's the SAME athlete profile, just built on their behalf —
              so that's surfaced as its own small expandable line instead
              of a separate persona, matching how it actually works. */}
          {PERSONAS.filter((p) => ONBOARDING_PERSONA_IDS.includes(p.id)).map((p) => {
            const Icon = p.Icon;
            const isSaving = savingId === p.id;
            const perkInfo = PERSONA_PERKS[p.id];
            const isExpanded = expandedId === p.id;
            return (
              <div
                key={p.id}
                className="group relative rounded-3xl ring-1 ring-white/12 bg-white/[0.04] hover:bg-white/[0.07] hover:ring-white/30 backdrop-blur-sm transition-all overflow-hidden"
                data-testid={`persona-welcome-${p.id}`}
              >
                <button
                  type="button"
                  onClick={() => pick(p.id)}
                  disabled={!!savingId}
                  className="w-full text-left p-6 sm:p-7 disabled:opacity-50 disabled:cursor-wait"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-white/[0.08] ring-1 ring-white/15 flex items-center justify-center text-white/85 group-hover:text-white group-hover:ring-white/40 transition shrink-0">
                      {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" strokeWidth={2.2} />}
                    </div>
                    <div>
                      <h3 className="font-display text-xl uppercase leading-tight text-white">{p.label}</h3>
                      <p className="text-[12px] leading-snug mt-0.5 text-white/50">{p.blurb}</p>
                    </div>
                  </div>
                  {perkInfo && (
                    <ul className="mt-5 space-y-1.5">
                      {perkInfo.perks.map((perk, i) => (
                        <li key={i} className="text-[12px] text-white/60 leading-snug flex gap-1.5">
                          <span className="text-white/30 shrink-0">•</span>{perk}
                        </li>
                      ))}
                    </ul>
                  )}
                  <span className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-white text-black font-black text-[12px] uppercase tracking-widest transition group-active:scale-[0.99]">
                    Continue <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
                {/* iter765 · Jay: a parent or fan operating the phone
                    should immediately see this is still the right card
                    for them — a small, clearly-labeled expandable line
                    instead of the generic "what do I get" chevron this
                    used to be when there were 3 competing tiles. */}
                {perkInfo?.note && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : p.id); }}
                      className="w-full flex items-center justify-center gap-1.5 py-3 text-[11px] font-bold uppercase tracking-widest text-white/50 hover:text-white/80 border-t border-white/10 transition"
                      aria-expanded={isExpanded}
                      data-testid={`persona-welcome-expand-${p.id}`}
                    >
                      Parent or guardian? <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="px-6 pb-5 -mt-1" data-testid={`persona-welcome-perks-${p.id}`}>
                        <p className="text-[12px] text-white/60 leading-relaxed text-center">{perkInfo.note}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PersonaWelcomeModal;
